import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Form, Input, Row, Col, message } from "antd";
import { CloseOutlined, RightOutlined } from "@ant-design/icons";
import styles from "./CustomerCreate.module.css";
import CustomButton from "../components/CustomButton";
import OCRUpload from "../components/OCRUpload";
import { CustomerSelectModal } from "../components/CustomerSelectModal";
import { TagSelector } from "../components/TagSelector";
import { useIOSConfirm } from "../hooks";

import { createBasicOrder, createCustomer, COMMON_DICTS, IdType, OrderStatus, useMetadataStore, useConfigStore } from "@ifa/utils";
import type { CustomerDTO } from "@ifa/utils";
import dayjs from "dayjs";
import { BirthDateInput } from "../components/form/BirthDateInput";
import { IdDocumentInput } from "../components/form/IdDocumentInput";
import { shouldAutoFillSalutationFromGender } from "../utils/salutationRules";

type FlowType = "order" | "customer";
type LocationState = { source?: FlowType } | null;

/**
 * 遮掩身份证号码
 * @param idNumber 身份证号码
 * @returns 遮掩后的身份证号码，例如：A1****6(7)
 */
function maskIdNumber(idNumber?: string): string {
  if (!idNumber || idNumber.length < 3) return idNumber || "";

  // 获取前2位
  const prefix = idNumber.substring(0, 2);
  // 获取最后2位（包括括号内容，如果有）
  const suffix = idNumber.substring(idNumber.length - 2);

  return `${prefix}****${suffix}`;
}

/**
 * 遮掩姓名
 * @param name 姓名
 * @returns 遮掩后的姓名，例如：劉*華
 */
function maskName(name?: string): string {
  if (!name || name.length < 2) return name || "";

  const firstChar = name.charAt(0);
  const lastChar = name.charAt(name.length - 1);

  return `${firstChar}*${lastChar}`;
}

/**
 * 获取身份证类型的显示名称
 */
function getIdTypeLabel(idType?: string): string {
  const idTypeMap: Record<string, string> = {
    HONGKONG_ID: "香港身份證",
    CHINA_ID: "中國身份證",
    PASSPORT: "護照",
    OTHER: "其他",
  };
  return idTypeMap[idType || ""] || idType || "";
}

/**
 * 根据身份证号解析出生日期和性别
 * @param idType 证件类型
 * @param idNumber 证件号码
 * @returns 解析出的出生日期和性别
 */
function parseIdNumber(idType?: string, idNumber?: string): { birthDate?: string; gender?: string } {
  if (!idNumber) return {};

  try {
    // 大陆身份证（18位）
    if (idType === IdType.IDCard && idNumber.length === 18) {
      const year = idNumber.substring(6, 10);
      const month = idNumber.substring(10, 12);
      const day = idNumber.substring(12, 14);
      const genderCode = parseInt(idNumber.substring(16, 17));

      const birthDate = `${year}-${month}-${day}`;
      const gender = genderCode % 2 === 0 ? "FEMALE" : "MALE";

      // 验证日期有效性
      if (dayjs(birthDate).isValid()) {
        return { birthDate, gender };
      }
    }

    // 香港身份证（格式：A123456(7) 或 AB123456(7)）
    if (idType === IdType.HongkongID) {
      // 香港身份证不包含出生日期和性别信息
      return {};
    }

    // 台湾身份证（10位，格式：A123456789）
    if (idType === IdType.TaiwanID && idNumber.length === 10) {
      const genderCode = idNumber.substring(1, 2);
      // 台湾身份证第二位：1=男性，2=女性
      if (genderCode === "1") {
        return { gender: "MALE" };
      } else if (genderCode === "2") {
        return { gender: "FEMALE" };
      }
      // 台湾身份证不包含出生日期
      return {};
    }

    // 澳门身份证
    if (idType === IdType.MacaoID) {
      // 澳门身份证不包含出生日期和性别信息
      return {};
    }
  } catch (error) {
    console.error("解析身份证号失败:", error);
  }

  return {};
}

type BasePayload = {
  chineseSurname?: string;
  chineseGivenName?: string;
  englishSurname?: string;
  englishGivenName?: string;
  birthDate?: string;
  gender?: string;
  salutation?: string;
  idType?: string;
  idNumber?: string;
  phone?: string;
};

type SubmitResult = {
  orderId?: number;
  customerId?: number;
  isCrossSellingExist?: boolean;
  fromCrossSelling?: boolean;
  existingCustomer?: {
    chineseSurname?: string;
    chineseGivenName?: string;
    idType?: string;
    idNumber?: string;
  };
  [key: string]: any;
};

type FlowConfig = {
  breadcrumbLabel: string;
  breadcrumbPath: string;
  closePath: string;
  submit: (payload: BasePayload, forceAdd: boolean) => Promise<SubmitResult>;
  onSuccess: (values: any, result: SubmitResult) => void;
  shouldHandleDuplicate?: (result: SubmitResult) => boolean;
  onDuplicate?: (result: SubmitResult, retry: () => void) => void;
};

export function CustomerCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const flow: FlowType = ((location.state as LocationState) || {})?.source === "customer" ? "customer" : "order";
  const [form] = Form.useForm();
  const idType = Form.useWatch("idType", form);
  const idNumber = Form.useWatch("idNumber", form);
  const gender = Form.useWatch("gender", form);
  const [loading, setLoading] = useState(false);
  const { selectMetadataList } = useMetadataStore();
  const [selectModalVisible, setSelectModalVisible] = useState(false);
  const { confirm } = useIOSConfirm();
  const brandId = useConfigStore((state) => state.brandId);
  const tenantCode = useConfigStore((state) => state.tenantProfile?.tenantCode);
  const shouldAutoFillSalutation = shouldAutoFillSalutationFromGender({
    brandId,
    tenantCode,
  });

  const getOptions = (code: string, fallback: any[] = []) => {
    const meta = selectMetadataList.find((m) => m.code === code);
    if (meta?.items?.length) {
      return meta.items.map((i) => ({ label: i.name || i.key, value: i.key }));
    }
    return fallback;
  };

  const genderOptions = getOptions("GENDER", COMMON_DICTS.gender);

  const flowConfig: Record<FlowType, FlowConfig> = {
    order: {
      breadcrumbLabel: "訂單",
      breadcrumbPath: "/orders",
      closePath: "/orders",
      submit: (payload, forceAdd) =>
        createBasicOrder({
          ...payload,
          forceAddOrder: forceAdd,
          draft: true,
          submit: false,
        }),
      shouldHandleDuplicate: (result) => Boolean(result.isCrossSellingExist),
      onDuplicate: (result, retry) => {
        console.log(result);
        const customer = result;
        const maskedName = maskName(`${customer?.chineseName || ""}`);
        const maskedId = maskIdNumber(customer?.idNumber);
        const idTypeLabel = getIdTypeLabel(customer?.idType);

        confirm({
          title: "已存在相同身份證資料的客戶",
          content: `已存在相同身份證資料的客戶：<span style="color: #ff4d4f; font-weight: 500;">「${maskedName}，${idTypeLabel}號碼：${maskedId}」</span>。您可更換此客戶/投保人投保，或修改身份證資料後再次驗證。`,
          okText: "立即更換客戶/投保人",
          cancelText: "使用此客戶資料投保",
          onOk: () => setSelectModalVisible(true), // Open selection modal
          onCancel: retry, // Retry with forceAdd (Use this customer data)
          dangerouslySetInnerHTML: true,
        });
      },
      onSuccess: (values, result) => {
        if (!result.orderId) {
          message.error("創建訂單失敗，未獲取到訂單編號");
          return;
        }
        message.success("客戶檔案建立成功");
        if (result.fromCrossSelling) {
          navigate(`/orders/${result.orderId}`, {
            state: {
              orderStatus: OrderStatus.Draft,
            },
          });
        } else {
          navigate(`/orders/${result.orderId}/edit`, {
            state: {
              customerType: "new",
              customerData: values,
              orderId: result.orderId,
              fromNewCustomer: true,
              orderStatus: OrderStatus.Draft,
            },
          });
        }
      },
    },
    customer: {
      breadcrumbLabel: "客戶",
      breadcrumbPath: "/customers",
      closePath: "/customers",
      submit: async (payload) => {
        const customerId = await createCustomer(payload as CustomerDTO);
        return { customerId };
      },
      onSuccess: (_values, result) => {
        message.success("客戶檔案建立成功");
        navigate(result.customerId ? `/customers/${result.customerId}` : "/customers", { replace: true });
      },
    },
  };

  const { closePath, submit, onSuccess, shouldHandleDuplicate, onDuplicate, breadcrumbLabel, breadcrumbPath } = flowConfig[flow];

  const handleBack = () => {
    navigate(-1);
  };

  const handleClose = () => {
    navigate(closePath);
  };

  const submitForm = async (forceAdd: boolean, fromCrossSelling = false) => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const basePayload: BasePayload = {
        chineseSurname: values.surnameZh,
        chineseGivenName: values.givenNameZh,
        englishSurname: values.surnameEn,
        englishGivenName: values.givenNameEn,
        gender: values.gender,
        salutation: values.salutation,
        idType: values.idType as any,
        idNumber: values.idNumber,
        birthDate: values.birthDate ? dayjs(values.birthDate).format("YYYY-MM-DD") : undefined,
        phone: values.mobilePrefix && values.mobileNumber ? `${values.mobilePrefix} ${values.mobileNumber}` : values.mobileNumber,
      };

      const result = await submit(basePayload, forceAdd);

      if (shouldHandleDuplicate?.(result)) {
        onDuplicate?.(result, () => submitForm(true, true));
        return;
      }

      onSuccess(values, { ...result, fromCrossSelling });
    } catch (error) {
      console.error("Operation failed:", error);
      if (error instanceof Error && error.message === "Validation failed") {
        // Validation error already handled
      } else {
        // message.error("創建失敗，請重試");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    submitForm(false);
  };

  /**
   * 處理 OCR 識別成功後的回調
   * 將識別結果填入表單
   * @param res OCR 識別結果
   */
  const handleOCRSuccess = (res: any) => {
    form.setFieldsValue({
      surnameZh: res.lastnameCn,
      givenNameZh: res.firstnameCn,
      surnameEn: res.lastnameEn,
      givenNameEn: res.firstnameEn,
      idType: res.type,
      idNumber: res.idNumber,
      gender: res.gender,
      salutation: shouldAutoFillSalutation ? res.gender : undefined,
      birthDate: res.birthDate ? dayjs(res.birthDate).format("YYYY-MM-DD") : undefined,
    });
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles["header-left"]}>
          <span className={styles["breadcrumb-link"]} onClick={() => navigate(breadcrumbPath)}>
            {breadcrumbLabel}
          </span>
          <RightOutlined className={styles["breadcrumb-separator"]} style={{ fontSize: 12 }} />
          <span className={styles["header-title"]}>新增客戶檔案</span>
        </div>
        <CloseOutlined className={styles["header-close"]} onClick={handleClose} />
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Warning Banner */}
        {/* <div className={styles["warning-banner"]}>不確定的部分，請選擇或者填寫 "待確認" ；帶 * 的為必填項。</div> */}

        <Form
          form={form}
          layout="vertical"
          initialValues={{
            idType: IdType.HongkongID,
            mobilePrefix: 852,
            gender: undefined,
            salutation: undefined,
          }}
        >
          {/* Section 1: Identity Document */}
          <div className={styles.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div className={styles["section-title"]} style={{ marginBottom: 0 }}>
                客戶/投保人資料 - 身份證明文件
              </div>
              <OCRUpload onSuccess={handleOCRSuccess} idType={idType} />
            </div>
            <Row gutter={24}>
              <Col xs={24} md={24}>
                <Form.Item label="客戶身份" layout="horizontal" labelAlign="left" labelCol={{ span: 12 }} wrapperCol={{ span: 12 }}>
                  <div style={{ paddingLeft: "12px" }}>
                    <IdDocumentInput
                      value={{ idType, idNumber }}
                      onChange={(value) => {
                        // 更新证件类型和号码
                        form.setFieldsValue({
                          idType: value?.idType,
                          idNumber: value?.idNumber,
                        });

                        // 根据身份证类型自动设置 hasHKIDCard
                        const currentHasHKIDCard = form.getFieldValue("hasHKIDCard");
                        if (!currentHasHKIDCard) {
                          if (value?.idType === IdType.HongKongID) {
                            form.setFieldValue("hasHKIDCard", "YES");
                          } else if (value?.idType) {
                            form.setFieldValue("hasHKIDCard", "NO");
                          }
                        }

                        // 根据身份证号自动填充出生日期、性别和称谓（仅在字段为空时）
                        if (value?.idNumber) {
                          const parsedInfo = parseIdNumber(value.idType, value.idNumber);
                          const currentBirthDate = form.getFieldValue("birthDate");
                          const currentGender = form.getFieldValue("gender");
                          const currentSalutation = form.getFieldValue("salutation");

                          // 仅在出生日期为空时填充
                          if (parsedInfo.birthDate && !currentBirthDate) {
                            form.setFieldValue("birthDate", parsedInfo.birthDate);
                          }
                          // 仅在性别为空时填充
                          if (parsedInfo.gender && !currentGender) {
                            form.setFieldValue("gender", parsedInfo.gender);
                          }
                          // 仅在称谓为空时填充（称谓与性别相同）
                          if (
                            parsedInfo.gender &&
                            !currentSalutation &&
                            shouldAutoFillSalutation
                          ) {
                            form.setFieldValue("salutation", parsedInfo.gender);
                          }
                        }
                      }}
                    />
                  </div>
                  <Form.Item name="idType" noStyle rules={[{ required: true, message: "請選擇身份證明文件類型" }]}>
                    <Input type="hidden" />
                  </Form.Item>
                  <Form.Item name="idNumber" noStyle rules={[{ required: true, message: "請輸入身份證明文件號碼" }]}>
                    <Input type="hidden" />
                  </Form.Item>
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Section 2: Basic Info */}
          <div className={styles.card}>
            <div className={styles["section-title"]}>客戶/投保人基本資料</div>
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="surnameZh"
                  label="姓氏(中文)"
                  rules={[
                    { required: true, message: "請輸入姓氏(中文)" },
                    { pattern: /^[\u4e00-\u9fa5-]+$/, message: "只能輸入中文或-" },
                  ]}
                >
                  <Input
                    placeholder="請輸入"
                    onBlur={(e) => {
                      const filtered = e.target.value?.replace(/[^\u4e00-\u9fa5-]/g, "");
                      form.setFieldValue("surnameZh", filtered);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="givenNameZh"
                  label="名字(中文)"
                  rules={[
                    { required: true, message: "請輸入名字(中文)" },
                    { pattern: /^[\u4e00-\u9fa5-]+$/, message: "只能輸入中文或-" },
                  ]}
                >
                  <Input
                    placeholder="請輸入"
                    onBlur={(e) => {
                      const filtered = e.target.value?.replace(/[^\u4e00-\u9fa5-]/g, "");
                      form.setFieldValue("givenNameZh", filtered);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="surnameEn"
                  label="姓氏(英文)"
                  rules={[
                    { required: true, message: "請輸入姓氏(英文)" },
                    { pattern: /^[a-zA-Z\s]+$/, message: "只能輸入英文" },
                  ]}
                  normalize={(v) => v?.replace(/[^a-zA-Z\s]/g, "")}
                >
                  <Input
                    inputMode="latin"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    autoComplete="off"
                    placeholder="請輸入"
                    onBlur={(e) => {
                      const val = e.target.value.replace(/[^a-zA-Z\s]/g, "").toUpperCase();
                      form.setFieldValue("surnameEn", val);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="givenNameEn"
                  label="名字(英文)"
                  rules={[
                    { required: true, message: "請輸入名字(英文)" },
                    { pattern: /^[a-zA-Z\s]+$/, message: "只能輸入英文" },
                  ]}
                  normalize={(v) => v?.replace(/[^a-zA-Z\s]/g, "")}
                >
                  <Input
                    inputMode="latin"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    autoComplete="off"
                    placeholder="請輸入"
                    onBlur={(e) => {
                      const val = e.target.value.replace(/[^a-zA-Z\s]/g, "").toUpperCase();
                      form.setFieldValue("givenNameEn", val);
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="birthDate" label="出生日期" rules={[{ required: true, message: "請選擇出生日期" }]}>
                  <BirthDateInput />
                </Form.Item>
              </Col>
              {/* <Col xs={24} md={12}>
                <Form.Item label="手提電話" required>
                  <Input.Group size="large" compact>
                    <Form.Item name="mobilePrefix" noStyle>
                      <CountrySelect size="large" type="phone" style={{ width: "30%" }} />
                    </Form.Item>
                    <Form.Item
                      name="mobileNumber"
                      noStyle
                      rules={[
                        { required: true, message: "請輸入手提電話號碼" },
                        { pattern: /^\d+$/, message: "只能輸入數字" },
                      ]}
                      normalize={(v) => v?.replace(/\D/g, "")}
                    >
                      <Input size="large" style={{ width: "70%" }} placeholder="輸入手提電話號碼" inputMode="numeric" />
                    </Form.Item>
                  </Input.Group>
                </Form.Item>
              </Col> */}
              <Col xs={24} md={12}>
                <Form.Item label="性別" required>
                  <TagSelector options={genderOptions} value={gender} onChange={(v) => form.setFieldValue("gender", v)} />
                  <Form.Item name="gender" noStyle rules={[{ required: true, message: "請選擇性別" }]}>
                    <Input type="hidden" />
                  </Form.Item>
                  <Form.Item name="salutation" noStyle>
                    <Input type="hidden" />
                  </Form.Item>
                </Form.Item>
              </Col>
            </Row>
          </div>
        </Form>
      </div>

      {/* Footer */}
      <div className={styles["footer-bar"]}>
        <CustomButton onClick={handleBack} disabled={loading} bgColor="#fff" color="#666" style={{ border: "1px solid #d9d9d9" }} width="120px" height="40px" fontSize="16px">
          返回上一頁
        </CustomButton>
        <CustomButton onClick={handleSubmit} loading={loading} bgColor="var(--theme-primary)" color="#fff" width="120px" height="40px" fontSize="16px">
          確定
        </CustomButton>
      </div>

      <CustomerSelectModal
        visible={selectModalVisible}
        onCancel={() => setSelectModalVisible(false)}
        onSelect={async (customer) => {
          setSelectModalVisible(false);
          // Use selected customer to create order
          setLoading(true);
          try {
            const result = await createBasicOrder({
              customerId: customer.customerId,
              draft: true,
              submit: false,
            });
            if (!result.orderId) {
              message.error("創建訂單失敗，未獲取到訂單編號");
              return;
            }
            message.success("訂單創建成功");
            navigate(`/orders/${result.orderId}`, {
              state: {
                customerType: "existing",
                customerData: customer,
                orderId: result.orderId,
                orderStatus: OrderStatus.Draft,
              },
            });
          } catch (error) {
            console.error("Failed to create order with existing customer:", error);
            // message.error("創建訂單失敗");
          } finally {
            setLoading(false);
          }
        }}
      />
    </div>
  );
}
